import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { CheckCircle, ArrowRight, Phone, Mail, ChevronRight, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppMenu from '../components/AppMenu';
import AppFooter from '../components/AppFooter';
import { getCategoryBySlug, QE_DEFAULTS } from '../lib/categoryConfig';
import { CatIcon } from '../lib/iconMap';
import apiClient from '../api/client';

const CONTACT_TEL_HREF = 'tel:0707388602';
const CONTACT_TEL_LABEL = '0707 388 602';
const CONTACT_EMAIL = 'kontakt@klarthemma.com';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return Math.round(n).toLocaleString('sv-SE');
}

// ── Quick Estimate card ───────────────────────────────────────────────────────

function QuickEstimate({ cat, settings }) {
  const qe = cat.qe;
  const ROT_RATE = settings?.ROT_RATE ?? QE_DEFAULTS.ROT_RATE;

  // Start with null to require explicit selection before showing price
  const [size, setSize] = useState(null);
  const [type, setType] = useState(null);
  const bothSelected = size !== null && type !== null;

  // Lead dialog
  const [showLead, setShowLead] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState({ name: '', phone: '', email: '', city: '' });

  const fees = {
    small: settings?.[`qe_${cat.slug}_fee_small`] ?? qe.feeSmall,
    standard: settings?.[`qe_${cat.slug}_fee_standard`] ?? qe.feeStandard,
    large: settings?.[`qe_${cat.slug}_fee_large`] ?? qe.feeLarge,
  };
  const span = settings?.[`qe_${cat.slug}_span`] ?? qe.spanDefault;
  const rotShare = settings?.[`qe_${cat.slug}_rot_labor_share`] ?? qe.rotLaborShare;

  const result = useMemo(() => {
    if (!size || !type) return null;
    const multKey = `qe_${cat.slug}_mult_${type}`;
    const fromSettings = settings?.[multKey];
    const fromConfig = qe.q2Options.find((o) => o.key === type)?.multiplier ?? 1.0;
    const multiplier =
      fromSettings != null && Number.isFinite(Number(fromSettings)) ? Number(fromSettings) : fromConfig;
    const rek = Math.round((fees[size] ?? fees.standard) * multiplier);
    const low = Math.round(rek * (1 - span));
    const high = Math.round(rek * (1 + span));
    const rot = Math.round(rek * rotShare * ROT_RATE);
    return { rek, low, high, rot };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- qe tied to cat.slug
  }, [size, type, fees.small, fees.standard, fees.large, span, rotShare, ROT_RATE, settings, cat.slug]);

  const navigate = useNavigate();
  const goToWizard = () => {
    navigate(`/wizard?category=${cat.slug}&qe_size=${size ?? 'standard'}&qe_type=${type ?? ''}`);
  };

  const sizeLabel = qe.q1Options.find((o) => o.key === size)?.label ?? size;
  const typeLabel = qe.q2Options.find((o) => o.key === type)?.label ?? type;

  const handleSubmitLead = async () => {
    if (!lead.name.trim() || !lead.phone.trim()) {
      toast.error('Namn och telefon krävs');
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post('/quick-leads', {
        kitchen_size: 'övrigt',
        high_cabinets: 'övrigt',
        price_model: 'fastpris',
        contact_name: lead.name.trim(),
        contact_phone: lead.phone.trim(),
        contact_email: lead.email.trim() || undefined,
        contact_city: lead.city.trim() || undefined,
        notes: `[Snabbkalkyl – ${cat.label}] Storlek: ${sizeLabel}, Typ: ${typeLabel}, Pris: ${result ? fmt(result.rek) : '?'} kr (spann ${result ? fmt(result.low) : '?'}–${result ? fmt(result.high) : '?'} kr)`,
        status: 'new',
      });
      setLeadDone(true);
      toast.success('Tack! Vi har tagit emot din förfrågan.');
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      toast.error(msg ? `Kunde inte skicka: ${msg}` : 'Kunde inte skicka. Försök igen eller ring oss.');
    } finally {
      setSubmitting(false);
    }
  };

  const openLead = () => {
    setLeadDone(false);
    setLead({ name: '', phone: '', email: '', city: '' });
    setShowLead(true);
  };

  const isKitchen = cat.slug === 'kok';

  return (
    <>
      <Card className="kh-category-estimate-card">
        <div className="kh-category-estimate-head">
          <p className="kh-eyebrow">PRISBILD</p>
          <h2>Få en första prisbild.</h2>
          <p>Välj storlek och arbete. Beloppet är en uppskattning som behöver stämmas av med oss.</p>
        </div>
        <CardContent className="kh-category-estimate-body space-y-6">
          {/* Q1 - Size */}
          <div>
            <p className="kh-category-question">{qe.q1Label}</p>
            <div className="grid grid-cols-3 gap-2">
              {qe.q1Options.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSize(opt.key)}
                  className={`border-2 rounded-xl p-3 text-center transition-all text-sm ${
                    size === opt.key
                      ? 'border-accent-dark bg-accent text-primary shadow-sm'
                      : 'border-slate-200 bg-white hover:border-accent'
                  }`}
                >
                  <div className="font-bold">{opt.label}</div>
                  <div className={`text-xs mt-0.5 ${size === opt.key ? 'text-primary/70' : 'text-muted-foreground'}`}>{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Q2 - Type */}
          <div>
            <p className="kh-category-question">{qe.q2Label}</p>
            <div className="grid grid-cols-2 gap-2">
              {qe.q2Options.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setType(opt.key)}
                  className={`border-2 rounded-xl px-3 py-2.5 text-left transition-all text-sm ${
                    type === opt.key
                      ? 'border-accent-dark bg-accent text-primary shadow-sm'
                      : 'border-slate-200 bg-white hover:border-accent'
                  }`}
                >
                  <span className="font-semibold">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Result — only shown once both questions are answered */}
          {bothSelected && result ? (
            <div className="kh-category-result space-y-1 animate-in fade-in duration-300" aria-live="polite">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Grov prisindikation</span>
                <span className="text-2xl font-black text-primary">{fmt(result.rek)} kr</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">Spann</span>
                <span className="text-sm font-semibold text-slate-600">{fmt(result.low)}–{fmt(result.high)} kr</span>
              </div>
              {isKitchen && (
                <div className="flex items-baseline justify-between border-t border-slate-200 mt-2 pt-2">
                  <span className="text-xs text-[var(--kh-blue)]">Möjligt ROT-avdrag</span>
                  <span className="text-sm font-bold text-[var(--kh-blue)]">−{fmt(result.rot)} kr</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">Inkl. moms. Slutligt pris och eventuellt ROT-avdrag stäms av i offerten.</p>
            </div>
          ) : (
            <div className="kh-category-result text-center">
              <p className="text-sm text-muted-foreground">Välj storlek och typ ovan för att se ett ungefärligt pris.</p>
            </div>
          )}

          <div className="space-y-2">
            <Button onClick={goToWizard} className="w-full bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold text-sm" size="lg">
              Beskriv arbetet <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button onClick={openLead} variant="outline" className="w-full text-sm">
              <Phone className="mr-2 h-4 w-4" /> Jag vill bli kontaktad
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lead dialog */}
      <Dialog open={showLead} onOpenChange={setShowLead}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          {leadDone ? (
            <div className="py-6 text-center space-y-3">
              <div className="mx-auto h-14 w-14 rounded-full bg-[var(--kh-blue)]/10 flex items-center justify-center">
                <CheckCircle className="h-7 w-7 text-[var(--kh-blue)]" />
              </div>
              <h3 className="text-lg font-bold">Tack för din förfrågan.</h3>
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-left">
                <p className="text-xs text-slate-500 mb-1">{cat.label}{sizeLabel ? ` · ${sizeLabel}` : ''}{typeLabel ? ` · ${typeLabel}` : ''}</p>
                {result ? (
                  <>
                    <p className="text-2xl font-black text-primary">{fmt(result.rek)} kr</p>
                    <p className="text-xs text-slate-400 mt-0.5">Spann: {fmt(result.low)}–{fmt(result.high)} kr</p>
                    {isKitchen && (
                      <p className="text-xs text-[var(--kh-blue)] mt-1 font-semibold">Möjligt ROT-avdrag: −{fmt(result.rot)} kr</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-500">Vi återkommer med ett pris baserat på din förfrågan.</p>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Vi återkommer när vi har gått igenom din förfrågan.</p>
              <Button className="w-full bg-[#374ef4] hover:bg-[#2a3fd4] text-white" onClick={() => setShowLead(false)}>
                Stäng
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Få ett bekräftat pris</DialogTitle>
                <DialogDescription className="flex items-start gap-1.5 text-sm">
                  <Shield className="h-3.5 w-3.5 text-[var(--kh-blue)] shrink-0 mt-0.5" />
                  Vi går igenom din förfrågan och återkommer med nästa steg.
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-xl bg-[#374ef4]/5 border border-[#374ef4]/20 px-4 py-3 space-y-2">
                <p className="text-xs font-semibold text-[#374ef4] uppercase tracking-wide">Din förfrågan</p>
                <p className="text-xs text-slate-500 leading-snug">{cat.label}{sizeLabel ? ` · ${sizeLabel}` : ''}{typeLabel ? ` · ${typeLabel}` : ''}</p>
                {result ? (
                  <>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 pt-1 border-t border-[#374ef4]/15">
                      <span className="text-sm text-slate-600">Rekommenderat pris</span>
                      <span className="text-xl font-black text-slate-900">{fmt(result.rek)} kr</span>
                    </div>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-xs text-slate-500">Spann</span>
                      <span className="text-sm font-semibold text-slate-700">{fmt(result.low)}–{fmt(result.high)} kr</span>
                    </div>
                    {isKitchen && (
                      <div className="flex flex-wrap items-baseline justify-between gap-2 pt-1 border-t border-[#374ef4]/15">
                        <span className="text-xs text-[var(--kh-blue)]">Möjligt ROT-avdrag</span>
                        <span className="text-sm font-bold text-[var(--kh-blue)]">−{fmt(result.rot)} kr</span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-500 pt-1 border-t border-[#374ef4]/15">Vi återkommer med ett pris baserat på din förfrågan.</p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="category-lead-name">Namn *</Label>
                  <Input
                    id="category-lead-name"
                    autoComplete="name"
                    placeholder="För- och efternamn"
                    value={lead.name}
                    onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="category-lead-phone">Telefon *</Label>
                  <Input
                    id="category-lead-phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="070-123 45 67"
                    inputMode="tel"
                    value={lead.phone}
                    onChange={(e) => setLead((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="category-lead-email">E-post</Label>
                  <Input
                    id="category-lead-email"
                    autoComplete="email"
                    type="email"
                    placeholder="din@email.se"
                    value={lead.email}
                    onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="category-lead-city">Ort</Label>
                  <Input
                    id="category-lead-city"
                    autoComplete="address-level2"
                    placeholder="t.ex. Malmö"
                    value={lead.city}
                    onChange={(e) => setLead((p) => ({ ...p, city: e.target.value }))}
                  />
                </div>
                <Button
                  className="w-full bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold"
                  onClick={handleSubmitLead}
                  disabled={submitting}
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Skicka förfrågan
                </Button>
                <p className="text-xs text-muted-foreground">Vi använder dina uppgifter för att besvara förfrågan. <a className="underline" href="/integritet">Läs integritetspolicyn</a>.</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Subcategory cards ─────────────────────────────────────────────────────────

function SubcategoryCards({ cards, categorySlug }) {
  const navigate = useNavigate();
  if (!cards || cards.length === 0) return null;

  return (
    <div className="mt-10">
      <h2 className="text-xl font-bold text-primary mb-4">Välj typ av arbete</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map((card) => (
          <button
            key={card.key}
            onClick={() =>
              card.isContact
                ? navigate('/kontakt')
                : navigate(`/wizard?category=${categorySlug}&subcategory=${card.key}`)
            }
            className="border border-slate-200 rounded-xl p-4 text-left hover:border-[#374ef4] hover:shadow-[0_0_0_1px_#374ef4] transition-all group bg-white"
          >
            <div className="h-9 w-9 rounded-lg bg-slate-100 group-hover:bg-[#374ef4]/10 flex items-center justify-center mb-3 transition-colors">
              <CatIcon name={card.icon} className="h-4 w-4 text-slate-500 group-hover:text-[#374ef4] transition-colors" />
            </div>
            <div className="font-bold text-sm text-primary group-hover:text-[#374ef4]">{card.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{card.sub}</div>
            {card.isContact && <Badge variant="outline" className="mt-2 text-xs">Kontakta oss</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CategoryPage({ slug }) {
  const cat = getCategoryBySlug(slug);
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    apiClient.get('/settings')
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, []);

  if (!cat) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Kategori hittades inte.</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{cat.seoTitle}</title>
        <meta name="description" content={cat.seoDescription} />
        <meta property="og:title" content={cat.seoTitle} />
        <meta property="og:description" content={cat.seoDescription} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`https://offert.klarthemma.com${cat.route}`} />
      </Helmet>

      <div className="kh-offer-shell kh-category-shell">
        <AppMenu />

        <main className="kh-category-main">
          {/* Hero */}
          <section className="kh-category-hero" aria-labelledby="kh-category-heading">
            <div className="kh-category-hero-copy">
              <p className="kh-eyebrow">TJÄNSTER · KLART HEMMA</p>
              <h1 id="kh-category-heading">{cat.label}</h1>
              <p>{cat.seoDescription}</p>
              <div className="kh-offer-hero-actions">
                <Button type="button" className="kh-button" onClick={() => navigate(`/wizard?category=${cat.slug}`)}>Beskriv ditt projekt <ArrowRight className="h-4 w-4" /></Button>
                <a className="kh-text-link" href="#prisbild">Se prisbilden <ChevronRight className="h-4 w-4" /></a>
              </div>
            </div>
            <div className="kh-category-hero-aside">
              <div className="kh-category-icon"><CatIcon name={cat.icon} className="h-9 w-9" /></div>
              <p>För ett hem som fungerar bättre.</p>
              <ul>{cat.trustBullets.slice(0, 3).map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
          </section>

          {/* Subcategory cards (VVS, El, Snickeri) */}
          {cat.subcategoryCards && (
            <SubcategoryCards cards={cat.subcategoryCards} categorySlug={cat.slug} />
          )}

          {/* Quick Estimate */}
          <section id="prisbild" className="kh-category-estimate">
            <QuickEstimate cat={cat} settings={settings} />
          </section>

          {/* Kontakt-strip */}
          <section className="kh-category-contact" aria-label="Kontakt">
            <h2 className="text-lg font-bold text-primary sm:text-xl mb-4">Hellre prata med oss?</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={CONTACT_TEL_HREF}
                className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-[#374ef4] hover:text-[#374ef4] transition-colors font-semibold text-sm text-primary"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#374ef4]/10 text-[#374ef4]">
                  <Phone className="h-4 w-4" aria-hidden />
                </span>
                {CONTACT_TEL_LABEL}
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-[#374ef4] hover:text-[#374ef4] transition-colors font-semibold text-sm text-primary break-all"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <Mail className="h-4 w-4" aria-hidden />
                </span>
                {CONTACT_EMAIL}
              </a>
            </div>
          </section>

          {/* FAQ */}
          <section className="kh-category-faq">
            <h2 className="text-2xl font-bold text-primary mb-4">Vanliga frågor</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {cat.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border border-slate-200 rounded-xl bg-white px-4">
                  <AccordionTrigger className="text-left font-semibold text-sm py-4">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* Final CTA */}
          <section className="kh-category-final-cta">
            <h2 className="text-2xl font-bold text-primary mb-2">Berätta vad du vill förändra.</h2>
            <p className="text-muted-foreground text-sm mb-6">Bilder och mått kan du lägga till i guiden. Vi återkommer med nästa steg.</p>
            <Button
              onClick={() => navigate(`/wizard?category=${cat.slug}`)}
              size="lg"
              className="bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-bold px-8"
            >
              Starta offertguiden <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </section>
        </main>

        <AppFooter />
      </div>
    </>
  );
}
